// Staff ↔ client message thread on the client file. One thread per client:
// the client's messages come from the portal, ours go out with an email nudge
// ("you have a new message") that never includes the text. Polls while mounted
// so a reply from the portal shows up without a refresh.
//
// Concurrency rules (judged): every GET carries a revision and only the newest
// applies; polling pauses while a send/delete is in flight; mutation results
// are merged by id, then a fresh GET reconciles. The parent mounts this with
// key={clientId} so a client switch always starts from clean state.
import { MessageSquare, Send, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { clients as clientsApi, type ClientMessage } from "../lib/api";

const POLL_MS = 20_000;
const MAX_CHARS = 4000;

function when(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}`;
}

function mergeById(cur: ClientMessage[], incoming: ClientMessage[]): ClientMessage[] {
  const map = new Map(cur.map((m) => [m.id, m]));
  for (const m of incoming) map.set(m.id, m);
  return [...map.values()].sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id));
}

export function ClientMessages({ clientId, clientName, currentUserId, canModerate, onUnreadChange }: {
  clientId: string;
  clientName: string;
  currentUserId: string | null;
  /** owner/manager — may delete any staff message; others only their own. */
  canModerate: boolean;
  /** Remaining unread client→staff count after each successful load (the GET marks them read). */
  onUnreadChange?: (unread: number) => void;
}) {
  const [messages, setMessages] = useState<ClientMessage[] | null>(null);
  const [canWrite, setCanWrite] = useState<boolean | null>(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const alive = useRef(true);
  const rev = useRef(0);          // newest GET wins
  const mutating = useRef(false); // polling pauses while a send/delete is in flight
  const loading = useRef(false);  // a poll never stacks on a slow GET
  const thread = useRef<HTMLDivElement | null>(null);
  const firstScroll = useRef(true);

  function nearBottom(): boolean {
    const el = thread.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  }
  function scrollToBottom() {
    const el = thread.current;
    if (el) el.scrollTop = el.scrollHeight;
  }

  async function load(): Promise<void> {
    const r = ++rev.current;
    loading.current = true;
    try {
      const res = await clientsApi.listMessages(clientId);
      if (!alive.current || r !== rev.current) return;
      setMessages(res.messages);
      setCanWrite(res.can_write);
      setLoadErr(null);
      onUnreadChange?.(res.unread);
      // Sample the reader's position now, not when the request started.
      if (firstScroll.current || nearBottom()) { firstScroll.current = false; requestAnimationFrame(scrollToBottom); }
    } catch (e: any) {
      if (!alive.current || r !== rev.current) return;
      setMessages((cur) => cur ?? []);
      setLoadErr(e?.body?.message || e?.message || "Could not load messages");
    } finally { if (r === rev.current) loading.current = false; }
  }

  useEffect(() => {
    alive.current = true;
    void load();
    const t = window.setInterval(() => { if (alive.current && !mutating.current && !loading.current && !document.hidden) void load(); }, POLL_MS);
    return () => { alive.current = false; rev.current++; window.clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  async function send() {
    const text = draft.trim();
    if (!text || text.length > MAX_CHARS || canWrite !== true) return;
    mutating.current = true; setBusy(true); setActionErr(null); setNotice(null);
    try {
      const r = await clientsApi.sendMessage(clientId, text);
      if (!alive.current) return;
      setMessages((cur) => mergeById(cur ?? [], [r.message]));
      setDraft("");
      setNotice(r.email_delivered ? `Sent. ${clientName} was emailed that a new message is waiting.` : "Sent. The client will see it in the portal (no email on file, or the email could not be delivered).");
      requestAnimationFrame(scrollToBottom);
    } catch (e: any) {
      if (!alive.current) return;
      if (e?.status === 403) setCanWrite(false);
      setActionErr(e?.body?.message || e?.message || "Could not send the message");
    } finally {
      mutating.current = false;
      if (alive.current) { setBusy(false); void load(); }
    }
  }

  async function remove(messageId: string) {
    mutating.current = true; setBusy(true); setActionErr(null);
    try {
      await clientsApi.deleteMessage(clientId, messageId);
      if (!alive.current) return;
      setMessages((cur) => (cur ?? []).filter((m) => m.id !== messageId));
      setConfirmDeleteId(null);
    } catch (e: any) {
      if (!alive.current) return;
      if (e?.status === 403) setCanWrite(false);
      setActionErr(e?.body?.message || e?.message || "Could not delete the message");
    } finally {
      mutating.current = false;
      if (alive.current) { setBusy(false); void load(); }
    }
  }

  const chars = draft.trim().length;

  return (
    <div className="client-messages">
      <section className="sub-panel">
        <div className="sub-heading" style={{ marginBottom: 8 }}>
          <MessageSquare size={18} aria-hidden="true" />
          <h3>Messages with {clientName}</h3>
        </div>
        <p className="muted small" style={{ marginTop: 0 }}>
          The client reads and replies in their portal. They get an email that a message is waiting — the text itself stays in here.
        </p>

        {loadErr && (
          <p className="error-text" role="alert">
            {loadErr} <button type="button" className="ghost-button" onClick={() => void load()} disabled={busy}>Retry</button>
          </p>
        )}

        <div ref={thread} className="client-messages__thread" role="log" aria-live="polite" aria-label="Message thread">
          {messages === null && <p className="muted small">Loading messages…</p>}
          {messages && messages.length === 0 && <p className="muted small">No messages yet. Say hello — it lands in their portal.</p>}
          {messages && messages.map((m) => {
            const mine = m.sender === "staff";
            const canDelete = canWrite === true && mine && (canModerate || (!!currentUserId && m.author_user_id === currentUserId));
            return (
              <div key={m.id} className={`client-messages__row ${mine ? "is-staff" : "is-client"}`}>
                <div className="client-messages__bubble">
                  <div className="client-messages__meta">
                    <strong>{mine ? (m.author_name || "Staff") : clientName}</strong>
                    <span className="muted small">{when(m.created_at)}{mine && m.read_at ? " · seen" : ""}</span>
                    {canDelete && (
                      confirmDeleteId === m.id ? (
                        <span className="client-messages__actions">
                          <button type="button" className="primary-button" style={{ background: "#b42318" }} onClick={() => remove(m.id)} disabled={busy}>Delete</button>
                          <button type="button" className="ghost-button" onClick={() => setConfirmDeleteId(null)} disabled={busy}>Keep</button>
                        </span>
                      ) : (
                        <span className="client-messages__actions">
                          <button type="button" className="ghost-button" title="Delete this message" aria-label="Delete this message" onClick={() => setConfirmDeleteId(m.id)} disabled={busy}>
                            <Trash2 size={13} aria-hidden="true" />
                          </button>
                        </span>
                      )
                    )}
                  </div>
                  <div className="client-messages__body">{m.body}</div>
                </div>
              </div>
            );
          })}
        </div>

        {actionErr && <p className="error-text" role="alert">{actionErr}</p>}
        {notice && <p className="muted small" role="status">{notice}</p>}

        {canWrite === true && (
          <div className="client-messages__composer">
            <textarea
              className="client-notes__editor"
              style={{ minHeight: 90 }}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={`Message ${clientName}…`}
              aria-label="New message"
              disabled={busy}
              maxLength={MAX_CHARS}
              onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); void send(); } }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
              <span className="muted small">{chars ? `${chars.toLocaleString()} / ${MAX_CHARS.toLocaleString()}` : "⌘/Ctrl + Enter to send"}</span>
              <button className="primary-button" onClick={send} disabled={busy || !chars || chars > MAX_CHARS}>
                <Send size={14} aria-hidden="true" /> {busy ? "Working…" : "Send message"}
              </button>
            </div>
          </div>
        )}
        {canWrite === false && <p className="muted small">Your role can read this thread but not reply.</p>}
      </section>
    </div>
  );
}
